import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, debounceTime, filter, takeUntil } from 'rxjs';
import {
  ClientsService,
  ListClientsParams,
} from 'src/app/services/clients/clients.service';
import {
  FeesService,
  ListFeesParams,
} from 'src/app/services/fees/fees.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import {
  ListPartnersParams,
  PartnerService,
} from 'src/app/services/partner/partner.service';
import {
  ProductDiscount,
  ProductDiscountService,
} from 'src/app/services/product-discount/product-discount.service';
import { ProductService } from 'src/app/services/product-service/product-service.service';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeDropdownComponent } from '../../../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';

@Component({
  selector: 'app-edit-product-discount-dialog',
  templateUrl: './edit-product-discount-dialog.component.html',
  styleUrls: ['./edit-product-discount-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    PayeeDropdownComponent,
    MatProgressSpinner,
    PayeeButtonComponent,
  ],
})
export class EditProductDiscountDialogComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private ref =
    inject<MatDialogRef<EditProductDiscountDialogComponent>>(MatDialogRef);
  private clientsService = inject(ClientsService);
  private partnerService = inject(PartnerService);
  private productService = inject(ProductService);
  private productDiscountService = inject(ProductDiscountService);
  private feesService = inject(FeesService);
  private helpersService = inject(HelpersService);

  private readonly destroy = new Subject<void>();

  productDiscount = inject<ProductDiscount>(MAT_DIALOG_DATA);

  readonly formGroup = this.fb.group({
    product_discountable_type: this.fb.control<
      ProductDiscount['product_discountable']['type']
    >(this.productDiscount.product_discountable.type, {
      validators: Validators.required,
      nonNullable: true,
    }),
    product_discountable: this.fb.control<
      ProductDiscount['product_discountable'] | null
    >(this.productDiscount.product_discountable, Validators.required),
    discount: this.fb.control(this.productDiscount.discount, {
      validators: [Validators.required, Validators.min(0)],
      nonNullable: true,
    }),
    discount_type: this.fb.control<ProductDiscount['discount_type']>(
      this.productDiscount.discount_type,
      { validators: Validators.required, nonNullable: true },
    ),
    fee: this.fb.control<ProductDiscount['fee'] | null>(
      this.productDiscount.fee,
      Validators.required,
    ),
    product: this.fb.control<ProductDiscount['product']>(
      this.productDiscount.product,
      { validators: Validators.required, nonNullable: true },
    ),
  });

  submitLoading = false;

  readonly productDiscountableTypeOptions: ProductDiscount['product_discountable']['type'][] =
    ['client', 'partner'];
  productDiscountableOptions: ProductDiscount['product_discountable'][] = [];
  readonly setProductDiscountableOptionsSubject = new Subject<string>();
  productDiscountableOptionsLoading = false;

  readonly discountTypeOptions: ProductDiscount['discount_type'][] = [
    'amount',
    'percentage',
  ];

  feeOptions: ProductDiscount['fee'][] = [];
  readonly setFeeOptionsSubject = new Subject<string>();
  feeOptionsLoading = false;

  productOptions: ProductDiscount['product'][] = [];

  get product_discountable_type() {
    return this.formGroup.controls.product_discountable_type;
  }
  get product_discountable() {
    return this.formGroup.controls.product_discountable;
  }
  get discount() {
    return this.formGroup.controls.discount;
  }
  get discount_type() {
    return this.formGroup.controls.discount_type;
  }
  get fee() {
    return this.formGroup.controls.fee;
  }
  get product() {
    return this.formGroup.controls.product;
  }

  async ngOnInit(): Promise<void> {
    this.product_discountable_type.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: () => {
          this.productDiscountableOptions = [];
          this.product_discountable.patchValue(null);
        },
      });

    this.product.valueChanges.pipe(takeUntil(this.destroy)).subscribe({
      next: () => {
        this.fee.patchValue(null);
        this.setFeeOptionsSubject.next('');
      },
    });

    this.setProductDiscountableOptionsSubject
      .pipe(
        takeUntil(this.destroy),
        filter((v) => !v || v.length >= 3),
        debounceTime(500),
      )
      .subscribe({
        next: async (filter) =>
          await this.setProductDiscountableOptions(filter),
      });

    this.setFeeOptionsSubject
      .pipe(
        takeUntil(this.destroy),
        filter((v) => !v || v.length >= 3),
        debounceTime(500),
      )
      .subscribe({
        next: async (filter) => await this.setFeeOptions(filter),
      });

    await this.setProductOptions();
    await this.setProductDiscountableOptions();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  productDiscountableDisplayWith(
    discountable?: ProductDiscount['product_discountable'],
  ): string {
    return discountable?.name ?? '';
  }

  productDisplayWith(product?: ProductDiscount['product']): string {
    return product?.name ?? '';
  }

  feeDisplayWith(fee?: ProductDiscount['fee']): string {
    return fee?.label ?? '';
  }

  async submit(): Promise<void> {
    if (this.submitLoading) {
      return;
    }
    if (this.formGroup.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.formGroup);
      return;
    }

    const {
      discount,
      discount_type,
      fee,
      product_discountable,
      product_discountable_type,
    } = this.formGroup.getRawValue();

    if (!fee || !product_discountable) {
      return;
    }

    try {
      this.submitLoading = true;

      const response = await this.productDiscountService.updateProductDiscount(
        this.productDiscount.id,
        {
          discount,
          discount_type,
          fee_id: Number(fee.id),
          product_discountable_id: product_discountable.id,
          product_discountable_type,
        },
      );

      this.ref.close(response);
    } catch (error) {
      console.error('Error while saving product discount', error);
    } finally {
      this.submitLoading = false;
    }
  }

  cancel(): void {
    this.ref.close();
  }

  private async setProductDiscountableOptions(filter?: string) {
    const type = this.product_discountable_type.value;
    if (!type) {
      this.productDiscountableOptions = [];
      return;
    }
    const loadTimeout = setTimeout(
      () => (this.productDiscountableOptionsLoading = true),
      200,
    );
    try {
      if (type === 'client') {
        const params: ListClientsParams = filter ? { name: filter } : {};
        const result = await this.clientsService.listClients(params);
        this.productDiscountableOptions = result.clients.map((c) => ({
          id: c.uuid,
          name: c.name,
          type: 'client' as const,
        }));
      } else {
        const params: ListPartnersParams = filter ? { name: filter } : {};
        const result = await this.partnerService.listPartners(params);
        this.productDiscountableOptions = result.partners.map((p) => ({
          ...p,
          type: 'partner' as const,
        }));
      }
    } catch (error) {
      console.error('Error while getting product discountables', error);
    } finally {
      clearTimeout(loadTimeout);
      this.productDiscountableOptionsLoading = false;
    }
  }

  private async setProductOptions(): Promise<void> {
    try {
      const response = await this.productService.getProducts();
      this.productOptions = response.products.map((p) => ({
        id: p.id,
        name: p.name,
        type: p.type,
      }));
    } catch (error) {
      console.error('Error while getting products', error);
    }
  }

  private async setFeeOptions(filter?: string): Promise<void> {
    if (!this.product.value) {
      return;
    }

    const loadTimeout = setTimeout(() => (this.feeOptionsLoading = true), 200);
    try {
      const params: ListFeesParams = {
        product_id: this.product.value.id,
      };
      if (filter) {
        params.label = filter;
      }
      const result = await this.feesService.listFees(params);
      this.feeOptions = result.fees;
    } catch (error) {
      console.error('Error while getting fees', error);
    } finally {
      clearTimeout(loadTimeout);
      this.feeOptionsLoading = false;
    }
  }
}
